import { AssetConfig } from '../types/index.js'

/**
 * Asset preloader utility for managing parallel loading of AR assets
 * Handles A-Frame library, MindAR.js library, compiled image targets, and 3D models
 * Based on requirement 1.5 - Updated for MindAR.js integration
 */
export class AssetPreloader {
  private assets: Map<string, AssetLoadInfo> = new Map()
  private loadingPromises: Map<string, Promise<void>> = new Map()
  private totalAssets: number = 0
  private loadedAssets: number = 0
  private onProgressCallback?: (progress: number) => void
  private onCompleteCallback?: () => void
  private onErrorCallback?: (error: AssetLoadError) => void
  private timeoutId?: number
  private config: AssetConfig

  constructor(config: AssetConfig) {
    this.config = config
  }

  /**
   * Start preloading all required assets in parallel
   * @param onProgress - Callback for progress updates (0-100)
   * @param onComplete - Callback when all assets are loaded
   * @param onError - Callback for asset loading errors
   */
  async preloadAssets(
    onProgress?: (progress: number) => void,
    onComplete?: () => void,
    onError?: (error: AssetLoadError) => void
  ): Promise<void> {
    this.onProgressCallback = onProgress
    this.onCompleteCallback = onComplete
    this.onErrorCallback = onError

    // Reset state
    this.assets.clear()
    this.loadingPromises.clear()
    this.loadedAssets = 0

    // Define assets to preload for MindAR.
    // For multi-character collections the individual videos are loaded on demand
    // (when a specific target is detected), so we only eagerly preload the
    // first character's video as a hint — and skip entirely if videoUrl is empty.
    const assetsToLoad = [
      { id: 'aframe-library', url: this.config.arLibraryUrl, type: 'script' as AssetType },
      { id: 'mindar-library', url: this.config.mindARLibraryUrl, type: 'script' as AssetType },
      { id: 'mindar-targets', url: this.config.imageTargetsUrl, type: 'binary' as AssetType },
      { id: 'roq-logo', url: this.config.roqLogoUrl, type: 'image' as AssetType },
      ...(this.config.videoUrl
        ? [{ id: 'roq-video', url: this.config.videoUrl, type: 'video' as AssetType }]
        : []),
      // Only preload the GLTF model when a URL is provided (ROQ client).
      // Collection clients leave animationModelUrl empty to skip this step
      // and avoid a 404 on the placeholder file that would freeze loading.
      ...(this.config.animationModelUrl
        ? [{ id: 'animation-model', url: this.config.animationModelUrl, type: 'model' as AssetType }]
        : [])
    ]

    this.totalAssets = assetsToLoad.length

    // Set up timeout
    this.setupTimeout()

    try {
      // Start loading all assets in parallel
      const loadPromises = assetsToLoad.map(asset => this.loadAsset(asset))
      
      // Wait for all assets to complete
      await Promise.all(loadPromises)
      
      // Clear timeout
      this.clearTimeout()
      
      // Notify completion
      if (this.onCompleteCallback) {
        this.onCompleteCallback()
      }
      
    } catch (error) {
      this.clearTimeout()
      
      const assetError: AssetLoadError = {
        type: 'LOAD_FAILED',
        message: error instanceof Error ? error.message : 'Unknown asset loading error',
        assetId: 'unknown',
        timestamp: Date.now()
      }
      
      if (this.onErrorCallback) {
        this.onErrorCallback(assetError)
      }
      
      throw error
    }
  }

  /**
   * Get current loading progress (0-100)
   */
  getLoadingProgress(): number {
    if (this.totalAssets === 0) return 0
    return Math.round((this.loadedAssets / this.totalAssets) * 100)
  }

  /**
   * Check if all assets are loaded
   */
  isReady(): boolean {
    return this.loadedAssets === this.totalAssets && this.totalAssets > 0
  }

  /**
   * Get asset loading information
   * @param assetId - ID of the asset to check
   */
  getAssetInfo(assetId: string): AssetLoadInfo | undefined {
    return this.assets.get(assetId)
  }

  /**
   * Get all asset loading information
   */
  getAllAssetInfo(): Map<string, AssetLoadInfo> {
    return new Map(this.assets)
  }

  /**
   * Cancel all ongoing asset loading
   */
  cancelLoading(): void {
    this.clearTimeout()
    
    // Cancel individual asset loads if possible
    this.loadingPromises.clear()
    
    // Reset state
    this.loadedAssets = 0
    this.totalAssets = 0
  }

  /**
   * Load a single asset with progress tracking and retry logic
   * @param asset - Asset configuration to load
   */
  private async loadAsset(asset: AssetToLoad): Promise<void> {
    const startTime = Date.now()
    const maxRetries = 3
    let retryCount = 0
    
    // Initialize asset info
    const assetInfo: AssetLoadInfo = {
      id: asset.id,
      url: asset.url,
      type: asset.type,
      status: 'loading',
      startTime,
      progress: 0
    }
    
    this.assets.set(asset.id, assetInfo)

    while (retryCount < maxRetries) {
      try {
        let loadPromise: Promise<void>

        switch (asset.type) {
          case 'script':
            loadPromise = this.loadScript(asset.url)
            break
          case 'image':
            loadPromise = this.loadImage(asset.url)
            break
          case 'video':
            loadPromise = this.loadVideo(asset.url)
            break
          case 'model':
            loadPromise = this.loadModel(asset.url)
            break
          case 'binary':
            loadPromise = this.loadBinaryFile(asset.url)
            break
          default:
            throw new Error(`Unsupported asset type: ${asset.type}`)
        }

        // Store promise for potential cancellation
        this.loadingPromises.set(asset.id, loadPromise)

        // Wait for asset to load with individual timeout
        await Promise.race([
          loadPromise,
          new Promise<never>((_, reject) => 
            setTimeout(() => reject(new Error('Asset load timeout')), 30000)
          )
        ])

        // Update asset info on success
        assetInfo.status = 'loaded'
        assetInfo.endTime = Date.now()
        assetInfo.progress = 100
        
        // Update global progress
        this.loadedAssets++
        this.updateProgress()
        
        console.log(`Asset loaded successfully: ${asset.id} (attempt ${retryCount + 1})`)
        return // Success - exit retry loop

      } catch (error) {
        retryCount++
        console.warn(`Asset load attempt ${retryCount} failed for ${asset.id}:`, error)
        
        // If this was the last retry, handle the error
        if (retryCount >= maxRetries) {
          // Update asset info on final error
          assetInfo.status = 'error'
          assetInfo.endTime = Date.now()
          assetInfo.error = error instanceof Error ? error.message : 'Unknown error'
          assetInfo.retryCount = retryCount
          
          const assetError: AssetLoadError = {
            type: this.categorizeAssetError(error),
            message: `Failed to load ${asset.type} asset after ${maxRetries} attempts: ${assetInfo.error}`,
            assetId: asset.id,
            timestamp: Date.now(),
            retryCount
          }
          
          if (this.onErrorCallback) {
            this.onErrorCallback(assetError)
          }
          
          throw error
        } else {
          // Wait before retry with exponential backoff
          const delay = Math.min(1000 * Math.pow(2, retryCount - 1), 5000)
          console.log(`Retrying asset load for ${asset.id} in ${delay}ms...`)
          await new Promise(resolve => setTimeout(resolve, delay))
        }
      } finally {
        this.loadingPromises.delete(asset.id)
      }
    }
  }

  /**
   * Load JavaScript library (A-Frame, MindAR) with comprehensive error handling
   * @param url - Script URL to load
   */
  private loadScript(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // Skip if the library globals are already present in the page — this
      // happens when index.html loads A-Frame / MindAR via static <script> tags
      // before the preloader runs.  Injecting a second copy at a different URL
      // causes "already registered" errors and corrupts MindAR internals.
      if (url.includes('aframe') && typeof window.AFRAME !== 'undefined') {
        console.log(`A-Frame already loaded (global present), skipping: ${url}`)
        resolve()
        return
      }
      if ((url.includes('mindar') || url.includes('mind-ar')) && typeof (window as any).MINDAR !== 'undefined') {
        console.log(`MindAR already loaded (global present), skipping: ${url}`)
        resolve()
        return
      }

      // Check if script is already loaded by matching src attribute
      const existingScript = document.querySelector(`script[src="${url}"]`)
      if (existingScript) {
        console.log(`Script already loaded: ${url}`)
        resolve()
        return
      }

      const script = document.createElement('script')
      script.src = url
      script.async = true
      script.crossOrigin = 'anonymous' // Enable CORS for better error reporting
      
      // Set up timeout
      const timeoutId = setTimeout(() => {
        script.remove()
        reject(new Error(`Script load timeout: ${url}`))
      }, 20000) // 20 second timeout for scripts
      
      script.onload = () => {
        clearTimeout(timeoutId)
        console.log(`Script loaded successfully: ${url}`)
        
        // Additional validation for specific libraries
        if (url.includes('aframe') && typeof window.AFRAME === 'undefined') {
          reject(new Error('A-Frame script loaded but AFRAME global not found'))
          return
        }
        
        if (url.includes('mindar') && typeof (window as any).MINDAR === 'undefined') {
          reject(new Error('MindAR script loaded but MINDAR global not found'))
          return
        }
        
        resolve()
      }
      
      script.onerror = (error) => {
        clearTimeout(timeoutId)
        script.remove()
        reject(new Error(`Failed to load script: ${url} - ${error}`))
      }
      
      document.head.appendChild(script)
    })
  }

  /**
   * Load image asset (marker image)
   * @param url - Image URL to load
   */
  private loadImage(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const img = new Image()
      
      img.onload = () => resolve()
      img.onerror = () => reject(new Error(`Failed to load image: ${url}`))
      
      img.src = url
    })
  }

  /**
   * Load video asset
   * @param url - Video URL to load
   */
  private loadVideo(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video')
      video.preload = 'metadata'
      video.muted = true // Required for autoplay
      video.playsInline = true // Required for iOS
      
      const timeoutId = setTimeout(() => {
        reject(new Error(`Video load timeout: ${url}`))
      }, 30000) // 30 second timeout for video
      
      video.onloadedmetadata = () => {
        clearTimeout(timeoutId)
        console.log(`Video metadata loaded: ${url} (duration: ${video.duration}s)`)
        resolve()
      }
      
      video.onerror = () => {
        clearTimeout(timeoutId)
        reject(new Error(`Failed to load video: ${url}`))
      }
      
      video.src = url
    })
  }

  /**
   * Load 3D model asset
   * @param url - Model URL to load
   */
  private loadModel(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // Use fetch to preload the model file
      fetch(url)
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`)
          }
          return response.blob()
        })
        .then(() => resolve())
        .catch(error => reject(new Error(`Failed to load model: ${error.message}`)))
    })
  }

  /**
   * Load binary file (MindAR compiled targets and other binary assets) with comprehensive error handling
   * @param url - Binary file URL to load
   */
  private loadBinaryFile(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => {
        controller.abort()
        reject(new Error('Binary file load timeout'))
      }, 25000) // 25 second timeout for binary files

      fetch(url, { 
        signal: controller.signal,
        cache: 'no-cache' // Ensure fresh download for critical AR assets
      })
        .then(response => {
          clearTimeout(timeoutId)
          
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`)
          }
          
          // Check content type if available
          const contentType = response.headers.get('content-type')
          if (url.endsWith('.mind') && contentType && !contentType.includes('application/octet-stream')) {
            console.warn(`Unexpected content type for MindAR file: ${contentType}`)
          }
          
          return response.arrayBuffer()
        })
        .then(arrayBuffer => {
          // Additional validation for MindAR .mind files
          if (url.endsWith('.mind')) {
            if (arrayBuffer.byteLength < 1000) {
              throw new Error('MindAR target file appears to be too small or corrupted')
            }
            
            // Basic validation of MindAR file structure
            const view = new Uint8Array(arrayBuffer)
            if (view.length < 100) {
              throw new Error('MindAR target file structure appears invalid')
            }
            
            console.log(`MindAR target loaded and validated: ${url} (${arrayBuffer.byteLength} bytes)`)
          } else {
            console.log(`Binary asset loaded: ${url} (${arrayBuffer.byteLength} bytes)`)
          }
          resolve()
        })
        .catch(error => {
          clearTimeout(timeoutId)
          
          if (error.name === 'AbortError') {
            reject(new Error('Binary file load was aborted due to timeout'))
          } else {
            reject(new Error(`Failed to load binary file: ${error.message}`))
          }
        })
    })
  }

  /**
   * Update loading progress and notify callback
   */
  private updateProgress(): void {
    const progress = this.getLoadingProgress()
    
    if (this.onProgressCallback) {
      this.onProgressCallback(progress)
    }
  }

  /**
   * Set up loading timeout
   */
  private setupTimeout(): void {
    this.timeoutId = window.setTimeout(() => {
      const timeoutError: AssetLoadError = {
        type: 'TIMEOUT',
        message: `Asset loading timed out after ${this.config.preloadTimeout}ms`,
        assetId: 'timeout',
        timestamp: Date.now()
      }
      
      if (this.onErrorCallback) {
        this.onErrorCallback(timeoutError)
      }
    }, this.config.preloadTimeout)
  }

  /**
   * Clear loading timeout
   */
  private clearTimeout(): void {
    if (this.timeoutId) {
      window.clearTimeout(this.timeoutId)
      this.timeoutId = undefined
    }
  }



  /**
   * Categorize asset loading errors for better error handling
   * @param error - The error that occurred during asset loading
   */
  private categorizeAssetError(error: any): AssetLoadError['type'] {
    if (error instanceof Error) {
      const message = error.message.toLowerCase()
      
      if (message.includes('timeout') || message.includes('aborted')) {
        return 'TIMEOUT'
      }
      
      if (message.includes('network') || message.includes('fetch')) {
        return 'NETWORK_ERROR'
      }
      
      if (message.includes('cors') || message.includes('cross-origin')) {
        return 'CORS_ERROR'
      }
      
      if (message.includes('corrupted') || message.includes('invalid') || message.includes('validation')) {
        return 'VALIDATION_ERROR'
      }
      
      if (message.includes('http') && (message.includes('404') || message.includes('500'))) {
        return 'NETWORK_ERROR'
      }
    }
    
    return 'LOAD_FAILED'
  }

  /**
   * Get MindAR target configuration for A-Frame scene
   */
  getMindARConfig(): MindARConfig {
    return {
      imageTargetSrc: this.config.imageTargetsUrl,
      maxTrack: 1,
      filterMinCF: 0.0001,
      filterBeta: 0.0005, // Optimized for faster response while maintaining smoothness
      targetPhysicalWidth: this.config.targetPhysicalWidth || 80
    }
  }
}

/**
 * Asset type enumeration
 */
type AssetType = 'script' | 'image' | 'video' | 'model' | 'binary'

/**
 * Asset loading status
 */
type AssetStatus = 'loading' | 'loaded' | 'error'

/**
 * Asset to load configuration
 */
interface AssetToLoad {
  id: string
  url: string
  type: AssetType
}

/**
 * Asset loading information
 */
interface AssetLoadInfo {
  id: string
  url: string
  type: AssetType
  status: AssetStatus
  startTime: number
  endTime?: number
  progress: number
  error?: string
  retryCount?: number
}

/**
 * Asset loading error information
 */
interface AssetLoadError {
  type: 'LOAD_FAILED' | 'TIMEOUT' | 'NETWORK_ERROR' | 'VALIDATION_ERROR' | 'CORS_ERROR'
  message: string
  assetId: string
  timestamp: number
  retryCount?: number
}

/**
 * MindAR configuration for A-Frame scene
 */
interface MindARConfig {
  imageTargetSrc: string
  maxTrack: number
  filterMinCF: number
  filterBeta: number
  targetPhysicalWidth: number
}